/**
 * TrackProgressSection — horizontal row of competency track progress pills.
 *
 * Shows completion % per track: Product, Sales, Compliance, Leadership.
 * EPIC-12-05 AC: track progress pills visible on My Learning page.
 */

import { useTranslation } from 'react-i18next';
import type { CompetencyTrack } from '../types';
import styles from './TrackProgressSection.module.css';

export interface TrackProgress {
  track: CompetencyTrack;
  completed: number;
  total: number;
}

interface TrackProgressSectionProps {
  tracks: TrackProgress[];
}

const TRACK_ORDER: CompetencyTrack[] = ['product', 'sales', 'compliance', 'leadership'];

/** Per-track accent palette (mirrors the landing-page card colors). */
const TRACK_THEME: Record<CompetencyTrack, { bg: string; fg: string }> = {
  product:    { bg: '#e8f0fb', fg: '#0055a5' },
  sales:      { bg: '#e6f4ed', fg: '#1a7a4a' },
  compliance: { bg: '#fef3e6', fg: '#c95f00' },
  leadership: { bg: '#f0e8fa', fg: '#6b3fa0' },
};

function TrackIcon({ track, color }: { track: CompetencyTrack; color: string }): JSX.Element {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (track === 'product') return (<svg {...common}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>);
  if (track === 'sales') return (<svg {...common}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>);
  if (track === 'compliance') return (<svg {...common}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>);
  return (<svg {...common}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>);
}

export function TrackProgressSection({ tracks }: TrackProgressSectionProps): JSX.Element {
  const { t } = useTranslation('training');

  const trackMap = new Map(tracks.map((tp) => [tp.track, tp]));

  return (
    <div className={styles.row} role="list" aria-label={t('agent.track.sectionLabel', { defaultValue: 'Track Progress' })}>
      {TRACK_ORDER.map((track) => {
        const tp = trackMap.get(track);
        const pct = tp && tp.total > 0 ? Math.round((tp.completed / tp.total) * 100) : 0;
        const label = t(`agent.track.${track}`, { defaultValue: track });
        const theme = TRACK_THEME[track];

        return (
          <div
            key={track}
            className={styles.card}
            role="listitem"
            data-track={track}
            aria-label={`${label} ${pct}%`}
          >
            <div className={styles.cardTop}>
              <span className={styles.iconChip} style={{ background: theme.bg }}>
                <TrackIcon track={track} color={theme.fg} />
              </span>
              <span className={styles.pct} style={{ color: theme.fg }}>{pct}%</span>
            </div>
            <span className={styles.trackName}>{label}</span>
            <div className={styles.bar}>
              <div className={styles.fill} style={{ width: `${pct}%`, background: theme.fg }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
